import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Popconfirm, Select, Skeleton, Space,
  Table, Tag, Typography, message,
} from 'antd';
import { DateField, StudentPickerField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection } from '../../../ui/forms';
import { PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import type { IssueTCRequest, TCConduct, TransferCertificateRow } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Transfer Certificate.
 * List of issued TCs + Issue-new modal (flips student.status = 'left') +
 * printable card via <details> reveal. Print CSS uses @media print for A4.
 */
export function TransferCertificatePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(false);
  const [printFor, setPrintFor] = useState<TransferCertificateRow | null>(null);

  const list = useQuery({
    queryKey: ['tcs', institution],
    queryFn: () => productApi.listTCs(institution),
    enabled: !!institution,
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div className="tc-no-print" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Transfer certificates</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Issuing marks the student as <b>left</b>. Numbers are monotonic per year per institution.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIssueOpen(true)}>Issue TC</Button>
      </div>

      <Card className="tc-no-print" size="small" title={`${list.data?.total ?? 0} certificates`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {list.data && list.data.rows.length === 0 && (
          <Empty description="No TCs issued yet." style={{ padding: 20 }} />
        )}
        {list.data && list.data.rows.length > 0 && (
          <Table<TransferCertificateRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={list.data.rows}
            columns={[
              { title: 'TC no.', dataIndex: 'tc_number', key: 'n', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Student', dataIndex: 'student_name', key: 's', render: (v: string) => <b>{v}</b> },
              { title: 'Adm no.', dataIndex: 'admission_number', key: 'a', width: 130, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Grade', key: 'g', width: 100, render: (_, r) => `${r.grade_at_leaving}${r.section_at_leaving ? `-${r.section_at_leaving}` : ''}` },
              { title: 'Issued', dataIndex: 'issue_date', key: 'i', width: 110 },
              { title: 'Reason', dataIndex: 'reason', key: 'r' },
              { title: 'Conduct', dataIndex: 'conduct', key: 'c', width: 100, render: (v: TCConduct) => <Tag color={v === 'excellent' ? 'success' : v === 'poor' ? 'error' : 'processing'}>{v}</Tag> },
              {
                key: 'action', width: 130, align: 'right',
                render: (_, r) => (
                  <Button size="small" icon={<PrinterOutlined />} onClick={() => setPrintFor(r)}>Print</Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      {printFor && <PrintView row={printFor} onClose={() => setPrintFor(null)} />}

      <IssueTCModal
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        institution={institution}
        onCreated={() => {
          setIssueOpen(false);
          void qc.invalidateQueries({ queryKey: ['tcs', institution] });
          void qc.invalidateQueries({ queryKey: ['students', institution] });
          void qc.invalidateQueries({ queryKey: ['briefing', institution] });
          void message.success('TC issued');
        }}
      />

      <style>{PRINT_CSS}</style>
    </Space>
  );
}

function PrintView({ row, onClose }: { row: TransferCertificateRow; onClose: () => void }) {
  return (
    <>
      <div className="tc-no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>Back to list</Button>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>Print (A4)</Button>
      </div>
      <div className="tc-page">
        <header className="tc-header">
          <div className="tc-inst-name">{row.institution_name}</div>
          <div className="tc-doc-title">Transfer Certificate</div>
          <div className="tc-number">No. <span className="tc-mono">{row.tc_number}</span></div>
        </header>

        <section className="tc-body">
          <p>
            This is to certify that <b>{row.student_name}</b> (Admission Number{' '}
            <span className="tc-mono">{row.admission_number}</span>), {row.gender},
            born on <b>{row.dob}</b>, was a bona fide student of this institution and studied
            in <b>{row.grade_at_leaving}{row.section_at_leaving ? `-${row.section_at_leaving}` : ''}</b>{' '}
            up to the date of issue mentioned below.
          </p>
          <div className="tc-facts">
            <div><span className="tc-label">Date of issue</span><span className="tc-val">{row.issue_date}</span></div>
            <div><span className="tc-label">Reason</span><span className="tc-val">{row.reason}</span></div>
            <div><span className="tc-label">Conduct</span><span className="tc-val">{row.conduct}</span></div>
          </div>
          {row.remarks && (
            <div className="tc-remarks">
              <div className="tc-label">Remarks</div>
              <div>{row.remarks}</div>
            </div>
          )}
        </section>

        <footer className="tc-footer">
          <div className="tc-sig"><div className="tc-line" /><div>Class teacher</div></div>
          <div className="tc-sig"><div className="tc-line" /><div>Principal</div></div>
          <div className="tc-sig"><div className="tc-line" /><div>Institution seal</div></div>
        </footer>

        <div className="tc-fine">
          Issued by {row.issued_by ?? 'the institution'} on {row.created_at.slice(0, 10)}.
          Duplicate copies bear the same number; original supersedes any reissue.
        </div>
      </div>
    </>
  );
}

type TCFormValues = { admission_number: string; issue_date: string; reason: string; conduct: TCConduct; remarks?: string };

function IssueTCModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <FormModal<TCFormValues>
      open={open} onClose={onClose}
      title="Issue transfer certificate" submitText="Issue"
      initial={{ issue_date: today, conduct: 'good' }}
      mutation={(v) => productApi.issueTC(institution, {
        admission_number: v.admission_number,
        issue_date: v.issue_date,
        reason: v.reason,
        conduct: v.conduct,
        remarks: v.remarks || undefined,
      } satisfies IssueTCRequest)}
      onSuccess={onCreated}
    >
      <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Issuing marks the student as 'left'. TC number is auto-issued." />
      <Field name="admission_number" label="Student" required>
        <StudentPickerField institution={institution} autoFocus />
      </Field>
      <FormSection cols={2}>
        <Field name="issue_date" label="Issue date" required>
          <DateField />
        </Field>
        <Field name="conduct" label="Conduct" required>
          <Select options={[
            { value: 'excellent', label: 'Excellent' },
            { value: 'good', label: 'Good' },
            { value: 'satisfactory', label: 'Satisfactory' },
            { value: 'poor', label: 'Poor' },
          ]} />
        </Field>
      </FormSection>
      <Field name="reason" label="Reason for leaving" required>
        <TextField placeholder="Family relocated to Bangalore" />
      </Field>
      <Field name="remarks" label="Remarks" optional>
        <Input.TextArea rows={3} />
      </Field>
    </FormModal>
  );
}

const PRINT_CSS = `
  .tc-page {
    background: #fff;
    color: #14213d;
    max-width: 794px;
    margin: 0 auto;
    padding: 34px 44px;
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    border: 1px solid #eeeef1;
    border-radius: 4px;
    min-height: 800px;
  }
  .tc-header { text-align: center; border-bottom: 2px solid #14213d; padding-bottom: 14px; margin-bottom: 24px; }
  .tc-inst-name { font-size: 24px; font-weight: 700; letter-spacing: 0.5px; }
  .tc-doc-title { font-size: 16px; margin-top: 6px; letter-spacing: 4px; text-transform: uppercase; color: #5b6478; }
  .tc-number { font-size: 12px; margin-top: 4px; color: #5b6478; }
  .tc-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
  .tc-body p { font-size: 14px; line-height: 1.7; margin-bottom: 20px; }
  .tc-facts { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; padding: 14px; background: #f7f7f5; border-radius: 4px; margin-bottom: 14px; }
  .tc-facts > div { display: flex; flex-direction: column; }
  .tc-label { font-size: 10.5px; color: #8a94a6; letter-spacing: 0.4px; text-transform: uppercase; font-weight: 600; }
  .tc-val { font-size: 13.5px; margin-top: 4px; font-weight: 500; }
  .tc-remarks { padding: 12px; border: 1px solid #eeeef1; border-radius: 4px; margin-bottom: 20px; font-size: 13px; }
  .tc-footer { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; margin-top: 60px; }
  .tc-sig { text-align: center; font-size: 11.5px; color: #5b6478; }
  .tc-line { border-top: 1px solid #14213d; margin: 40px 24px 8px; }
  .tc-fine { margin-top: 30px; font-size: 10.5px; color: #8a94a6; text-align: center; font-style: italic; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { background: #fff; }
    .tc-no-print, .ant-message, .ant-notification { display: none !important; }
    .tc-page { box-shadow: none; border: none; margin: 0; padding: 0; max-width: none; }
  }
`;
